/* 여행 코스 미리보기 (/ds/travel-course)
 *
 * 기획서: docs/travel-course-plan-ko.md
 *
 * 아직 서버가 없어 실제 화면에는 붙지 않았습니다. 붙었을 때 어떻게 보이는지
 * 여기서 확인합니다. 가짜 데이터는 이 파일에만 둡니다.
 *
 * 세 화면을 탭으로 넘겨 봅니다.
 *   A. 둘러보기 — 여행지를 고른 직후. "사람들은 이렇게 다녀왔어요"
 *   B. 코스 상세 — 카드를 누르면. Day 별 일정 + 따라하기
 *   C. 코스 등록 — 여행이 끝난 뒤. 이미 만든 일정을 코스로 남기기
 *
 * 비어 있을 때(코스 0건)도 같이 확인할 수 있게 토글을 뒀습니다 — 초기에는
 * 그게 기본 상태이고, 빈 섹션을 숨기는 동작이 설계대로인지 봐야 합니다.
 */
import { useState } from 'react';
import { useSearchParams } from 'react-router';
import {
    AppScreen, Badge, Button, ButtonFooter, CardRail, Chip, EmptyState,
    PlaceCard, Section, SectionHeader, StarRating, TextField, TextArea,
} from 'components/ds';
import { IcGlobe, IcPin, IcUsers } from 'components/common/icons/EmojiIcons';

const IMG = (seed: string) => `https://picsum.photos/seed/${seed}/600/600`;

/* ── 가짜 데이터 ───────────────────────────────────────────── */

interface Course {
    id: string;
    title: string;
    summary: string;
    days: number;
    placeCount: number;
    rating: number;
    ratingCount: number;
    copiedCount: number;
    creator: string;
    thumb: string;
}

const COURSES: Course[] = [
    {
        id: 'c1', title: '속초 바다랑 설악산 3일',
        summary: '차 없이도 다닐 수 있어요. 둘째 날 설악산은 케이블카를 미리 예약해두는 게 좋습니다.',
        days: 3, placeCount: 7, rating: 4.6, ratingCount: 12, copiedCount: 34,
        creator: '쎈파파', thumb: IMG('sokcho'),
    },
    {
        id: 'c2', title: 'DMZ 하루 코스',
        summary: '오전에 출발해 저녁 전에 돌아옵니다. 임진각에서 점심이 편해요.',
        days: 1, placeCount: 4, rating: 4.2, ratingCount: 5, copiedCount: 11,
        creator: 'hahn', thumb: IMG('dmz'),
    },
    {
        id: 'c3', title: '가평 계곡에서 쉬다 오기',
        summary: '아이들과 가기 좋았습니다. 물놀이 뒤 근처 카페가 조용해요.',
        days: 2, placeCount: 5, rating: 5.0, ratingCount: 2, copiedCount: 3,
        creator: '아이폰테스트', thumb: IMG('gapyeong'),
    },
];

/** TourAPI 여행코스 — 있는 지역에만 (서울·제주는 0건) */
const TOUR_COURSES = [
    { id: 't1', name: '서해 석양부터 야경까지 드라이브 코스', thumb: IMG('sunset') },
    { id: 't2', name: '알짜배기 명소만 골라 놓은 DMZ 핫플레이스', thumb: IMG('dmz2') },
];

/** 직접 고르기 — 전 지역 항상 있음 */
const SPOTS = [
    { id: 's1', name: '남한산성', thumb: IMG('fortress') },
    { id: 's2', name: '에버랜드', thumb: IMG('park') },
    { id: 's3', name: '수원화성', thumb: IMG('hwaseong') },
    { id: 's4', name: '포천 아트밸리', thumb: IMG('artvalley') },
];

const SIGUNGU = ['부천시', '수원시', '가평군', '고양시', '파주시', '용인시', '남양주시', '연천군'];

/** 코스 상세의 Day 별 일정 */
const ITINERARY = [
    {
        day: 1, items: [
            { time: '09:00', name: '속초해변', cat: '관광', thumb: IMG('beach') },
            { time: '12:30', name: '대포항', cat: '식사', thumb: IMG('port') },
            { time: '18:00', name: '더스테이 호텔', cat: '숙박', thumb: IMG('hotel1') },
        ],
    },
    {
        day: 2, items: [
            { time: '08:00', name: '설악산 케이블카', cat: '관광', thumb: IMG('mountain') },
            { time: '13:00', name: '권금성', cat: '관광', thumb: IMG('castle') },
            { time: '19:00', name: '더스테이 호텔', cat: '숙박', thumb: IMG('hotel1') },
        ],
    },
    {
        day: 3, items: [
            { time: '10:00', name: '영금정', cat: '관광', thumb: IMG('rock') },
        ],
    },
];

/* ── 화면 ──────────────────────────────────────────────────── */

type Tab = 'browse' | 'days' | 'detail' | 'register';

/** 화면마다 실제 상단바 제목 — 미리보기 티를 내지 않습니다 */
const TITLES: Record<Tab, string> = {
    browse: '어디를 다녀볼까요',
    days: '일정 짜기',
    detail: '코스',
    register: '코스로 남기기',
};

export const TravelCoursePreview = () => {
    /* 탭·상태를 URL 로 지정할 수 있게 둡니다.
       기획 문서에 넣을 화면을 헤드리스 브라우저로 찍을 때 클릭을 할 수 없어서,
       ?tab=detail 처럼 주소만으로 각 화면에 바로 닿아야 합니다.
       ?empty=1 은 코스가 0건인 초기 상태입니다. */
    const [params] = useSearchParams();
    const urlTab = params.get('tab') as Tab | null;
    const [tab] = useState<Tab>(
        urlTab && urlTab in TITLES ? urlTab : 'browse',
    );
    const [hasCourses] = useState(params.get('empty') !== '1');
    const [title, setTitle] = useState('속초 바다랑 설악산 다 보는 3일');
    const [summary, setSummary] = useState('차 없이도 다닐 수 있어요');
    const [rating, setRating] = useState(0);

    return (
        <AppScreen title={TITLES[tab]} bar="sub" hasFooter>
            {tab === 'browse' && <BrowseScreen hasCourses={hasCourses} />}
            {tab === 'days' && <DaysScreen />}
            {tab === 'detail' && <DetailScreen rating={rating} setRating={setRating} />}
            {tab === 'register' && (
                <RegisterScreen
                    title={title} setTitle={setTitle}
                    summary={summary} setSummary={setSummary}
                />
            )}
        </AppScreen>
    );
};

/* ── A. 둘러보기 ───────────────────────────────────────────── */

const BrowseScreen = ({ hasCourses }: { hasCourses: boolean }) => (
    <>
        <Section spacing="tight">
            <h2 style={{
                margin: 0, fontSize: 'var(--t-font-title)', fontWeight: 'var(--t-weight-black)',
                letterSpacing: 'var(--t-tracking-title)',
            }}>
                경기 여행, 어떻게 다녀볼까요?
            </h2>
            <p style={{ margin: 'var(--t-space-2) 0 0', color: 'var(--t-ink-3)', fontSize: 'var(--t-font-label)' }}>
                나중에 정해도 돼요
            </p>
        </Section>

        {/* 우리 코스 — 가장 믿을 만한 재료라 맨 위입니다.
            코스가 없으면 섹션째 숨깁니다(빈 영역은 고장처럼 보입니다). */}
        {hasCourses && (
            <Section divided>
                <SectionHeader title="사람들은 이렇게 다녀왔어요" sub="실제로 다녀온 기록이에요" />
                {/* 카드 아래 별점이 붙어 감싸야 합니다. min-width:0 이 없으면
                    긴 코스 제목이 카드를 basis 너머로 밀어 폭이 제각각이 됩니다. */}
                <CardRail>
                    {COURSES.map(c => (
                        <div key={c.id} style={{ flex: '0 0 172px', minWidth: 0 }}>
                            <PlaceCard
                                wide
                                imageUrl={c.thumb}
                                name={c.title}
                                meta={`${c.days === 1 ? '당일' : `${c.days - 1}박${c.days}일`} · ${c.placeCount}곳`}
                                evidence={`${c.creator} · ${c.copiedCount}명 따라함`}
                                onClick={() => { /* 미리보기 */ }}
                            />
                            <div style={{ marginTop: 'var(--t-space-2)' }}>
                                <StarRating value={c.rating} count={c.ratingCount} source="CREW" />
                            </div>
                        </div>
                    ))}
                </CardRail>
            </Section>
        )}

        {/* TourAPI 코스 — 있는 지역에만. 서울·제주는 0건이라 안 보입니다. */}
        {hasCourses && (
            <Section divided>
                <SectionHeader title="추천 코스" sub="한국관광공사" />
                <CardRail>
                    {TOUR_COURSES.map(c => (
                        <PlaceCard key={c.id} wide imageUrl={c.thumb} name={c.name} meta="추천 코스" />
                    ))}
                </CardRail>
            </Section>
        )}

        {/* 직접 고르기 — 전 지역 항상 있습니다. 코스가 없어도 여기서 막히지 않습니다. */}
        <Section divided>
            <SectionHeader title="직접 고르기" sub="가고 싶은 곳이 있다면" />
            <p style={{
                margin: '0 0 var(--t-space-3)', color: 'var(--t-ink-3)',
                fontSize: 'var(--t-font-label)',
            }}>
                유명 관광지
            </p>
            <CardRail>
                {SPOTS.map(s => (
                    <PlaceCard key={s.id} imageUrl={s.thumb} name={s.name} meta="관광" />
                ))}
            </CardRail>

            <p style={{
                margin: 'var(--t-space-5) 0 var(--t-space-3)', color: 'var(--t-ink-3)',
                fontSize: 'var(--t-font-label)',
            }}>
                시 · 군으로 좁히기
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--t-space-2)' }}>
                {SIGUNGU.map(s => <Chip key={s} label={s} />)}
            </div>
        </Section>

        {!hasCourses && (
            <Section divided>
                <EmptyState
                    icon={<IcGlobe size={28} />}
                    title="아직 이 지역 코스가 없어요"
                    body="여행을 다녀온 뒤 코스로 남기면 다음 사람이 따라갈 수 있어요."
                />
            </Section>
        )}

        <ButtonFooter>
            <Button block variant="secondary">건너뛰기</Button>
        </ButtonFooter>
    </>
);

/* ── A-2. 일정 짜기 (담은 곳을 Day 별로) ───────────────────── */

/* 담을 때마다 "며칠째?" 를 물으면 다섯 곳에 다섯 번 묻게 됩니다.
   일단 담고 여기서 한 번에 나눕니다. 미배정도 그대로 둘 수 있습니다. */
const PICKED = [
    { id: 'p1', name: '속초해변', cat: '관광', day: 1, thumb: IMG('beach') },
    { id: 'p2', name: '대포항', cat: '식사', day: 1, thumb: IMG('port') },
    { id: 'p3', name: '설악산 케이블카', cat: '관광', day: 2, thumb: IMG('mountain') },
    { id: 'p4', name: '더스테이 호텔', cat: '숙박', day: 2, thumb: IMG('hotel1') },
    { id: 'p5', name: '영금정', cat: '관광', day: 0, thumb: IMG('rock') },
    { id: 'p6', name: '중앙시장', cat: '식사', day: 0, thumb: IMG('market') },
];

/** 미리보기용 날짜 — 09/28 출발 2박 3일 */
const DAY_DATES = ['09/28 (월)', '09/29 (화)', '09/30 (수)'];

const DayRow = ({ p }: { p: typeof PICKED[0] }) => (
    <li style={{
        display: 'flex', alignItems: 'center', gap: 'var(--t-space-3)',
        padding: 'var(--t-space-3) 0', borderBottom: '1px solid var(--t-line)',
    }}>
        <span style={{
            flex: 'none', width: 48, height: 48, overflow: 'hidden',
            background: 'var(--t-surface-muted)', border: '1px solid var(--t-line)',
        }}>
            <img src={p.thumb} alt="" loading="lazy"
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: 'var(--t-font-caption)', color: 'var(--t-ink-3)' }}>
                {p.cat}
            </span>
            <span style={{
                display: 'block', fontSize: 'var(--t-font-body)', fontWeight: 'var(--t-weight-bold)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
                {p.name}
            </span>
        </span>
        {/* 날짜를 바꾸는 자리 — 끌어 옮기지 않고 눌러서 고릅니다(한 손 조작).
            Day 가 늘면 칩이 넘치므로 줄바꿈을 허용하고 폭을 제한합니다. */}
        <span style={{
            flex: 'none', display: 'flex', gap: 4, flexWrap: 'wrap',
            justifyContent: 'flex-end', maxWidth: 132,
        }}>
            {[1, 2, 3].map(d => (
                <Chip key={d} label={String(d)} selected={p.day === d} />
            ))}
        </span>
    </li>
);

const DaysScreen = () => (
    <>
        <Section spacing="tight">
            <h2 style={{
                margin: 0, fontSize: 'var(--t-font-title)', fontWeight: 'var(--t-weight-black)',
                letterSpacing: 'var(--t-tracking-title)',
            }}>
                담은 곳을 며칠째에 넣을까요?
            </h2>
            <p style={{ margin: 'var(--t-space-2) 0 0', color: 'var(--t-ink-3)', fontSize: 'var(--t-font-label)' }}>
                지금 안 정해도 돼요. 나중에 바꿀 수 있어요
            </p>
        </Section>

        {[1, 2, 3].map(d => {
            const items = PICKED.filter(p => p.day === d);
            return (
                <Section divided key={d}>
                    <SectionHeader title={`Day ${d}`} sub={`${DAY_DATES[d - 1]} · ${items.length}곳`} />
                    {items.length === 0 ? (
                        <p style={{ margin: 0, color: 'var(--t-ink-3)', fontSize: 'var(--t-font-label)' }}>
                            아직 없어요
                        </p>
                    ) : (
                        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                            {items.map(p => <DayRow key={p.id} p={p} />)}
                        </ul>
                    )}
                </Section>
            );
        })}

        {/* 미배정 — 강제로 나누게 하지 않습니다 */}
        <Section divided last>
            <SectionHeader title="아직 안 정한 곳" sub="상세에서 언제든 옮길 수 있어요" />
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {PICKED.filter(p => p.day === 0).map(p => <DayRow key={p.id} p={p} />)}
            </ul>
        </Section>

        <ButtonFooter>
            <Button block>이대로 저장</Button>
        </ButtonFooter>
    </>
);

/* ── B. 코스 상세 ──────────────────────────────────────────── */

const DetailScreen = ({
    rating, setRating,
}: { rating: number; setRating: (n: number) => void }) => (
    <>
        <Section spacing="tight">
            <h2 style={{
                margin: 0, fontSize: 'var(--t-font-title)', fontWeight: 'var(--t-weight-black)',
                letterSpacing: 'var(--t-tracking-title)',
            }}>
                속초 바다랑 설악산 3일
            </h2>
            <div style={{
                display: 'flex', alignItems: 'center', gap: 'var(--t-space-3)',
                marginTop: 'var(--t-space-2)', flexWrap: 'wrap',
            }}>
                <span style={{ color: 'var(--t-ink-3)', fontSize: 'var(--t-font-label)' }}>
                    쎈파파 · 2026년 9월
                </span>
                <StarRating value={4.6} count={12} source="CREW" />
            </div>
            <p style={{
                margin: 'var(--t-space-4) 0 0', color: 'var(--t-ink-2)',
                fontSize: 'var(--t-font-body)', lineHeight: 1.6,
            }}>
                차 없이도 다닐 수 있어요. 둘째 날 설악산은 케이블카를 미리 예약해두는 게 좋습니다.
            </p>
            <div style={{ display: 'flex', gap: 'var(--t-space-2)', marginTop: 'var(--t-space-4)' }}>
                <Badge tone="outline">2박 3일</Badge>
                <Badge tone="outline">7곳</Badge>
                <Badge tone="outline">34명 따라함</Badge>
            </div>
        </Section>

        {ITINERARY.map(d => (
            <Section divided key={d.day}>
                <SectionHeader title={`Day ${d.day}`} />
                <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                    {d.items.map((it, i) => (
                        <li key={i} style={{
                            display: 'flex', alignItems: 'center', gap: 'var(--t-space-4)',
                            padding: 'var(--t-space-3) 0',
                            borderBottom: i < d.items.length - 1 ? '1px solid var(--t-line)' : 'none',
                        }}>
                            <span style={{
                                flex: 'none', width: 48, fontSize: 'var(--t-font-label)',
                                fontWeight: 'var(--t-weight-bold)', fontVariantNumeric: 'tabular-nums',
                            }}>
                                {it.time}
                            </span>
                            <span style={{
                                flex: 'none', width: 56, height: 56, overflow: 'hidden',
                                background: 'var(--t-surface-muted)', border: '1px solid var(--t-line)',
                            }}>
                                <img src={it.thumb} alt="" loading="lazy"
                                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                            </span>
                            <span style={{ flex: 1, minWidth: 0 }}>
                                <span style={{
                                    display: 'block', fontSize: 'var(--t-font-caption)', color: 'var(--t-ink-3)',
                                }}>
                                    {it.cat}
                                </span>
                                <span style={{
                                    display: 'block', fontSize: 'var(--t-font-body)',
                                    fontWeight: 'var(--t-weight-bold)', overflow: 'hidden',
                                    textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                }}>
                                    {it.name}
                                </span>
                            </span>
                        </li>
                    ))}
                </ul>
            </Section>
        ))}

        <Section divided>
            <Button block variant="secondary">
                <IcPin size={14} /> 지도로 보기
            </Button>
        </Section>

        {/* 별점 — 따라한 사람만, 그 여행이 끝난 뒤에 묻습니다 */}
        <Section divided last>
            <SectionHeader title="따라한 코스는 어땠나요?" sub="따라해 본 사람만 별점을 남길 수 있어요" />
            <div style={{ display: 'flex', gap: 'var(--t-space-2)', padding: 'var(--t-space-3) 0' }}>
                {[1, 2, 3, 4, 5].map(n => (
                    <button
                        key={n}
                        type="button"
                        onClick={() => setRating(n)}
                        aria-label={`${n}점`}
                        style={{
                            border: 0, background: 'none', cursor: 'pointer', padding: 0,
                            fontSize: 28, lineHeight: 1,
                            color: n <= rating ? 'var(--t-ink-1)' : 'var(--t-line-strong)',
                        }}
                    >
                        ★
                    </button>
                ))}
            </div>
            <TextArea
                id="course-review"
                label="한 줄 후기 (선택)"
                placeholder="따라가 본 느낌을 남겨 주세요"
                value=""
                onChange={() => { /* 미리보기 */ }}
                rows={3}
            />
        </Section>

        <ButtonFooter>
            <Button block>내 여행으로 담기</Button>
        </ButtonFooter>
    </>
);

/* ── C. 코스 등록 ──────────────────────────────────────────── */

const RegisterScreen = ({
    title, setTitle, summary, setSummary,
}: {
    title: string; setTitle: (v: string) => void;
    summary: string; setSummary: (v: string) => void;
}) => (
    <>
        <Section spacing="tight">
            <h2 style={{
                margin: 0, fontSize: 'var(--t-font-title)', fontWeight: 'var(--t-weight-black)',
                letterSpacing: 'var(--t-tracking-title)',
            }}>
                이 여행을 코스로 남기기
            </h2>
            <p style={{ margin: 'var(--t-space-2) 0 0', color: 'var(--t-ink-3)', fontSize: 'var(--t-font-label)' }}>
                다음 사람이 따라갈 수 있어요
            </p>
        </Section>

        {/* 이미 만든 일정이 그대로 코스가 됩니다 — 새로 입력할 게 거의 없습니다 */}
        <Section divided>
            <div style={{ display: 'flex', gap: 'var(--t-space-4)', alignItems: 'center' }}>
                <span style={{
                    flex: 'none', width: 72, height: 72, overflow: 'hidden',
                    background: 'var(--t-surface-muted)', border: '1px solid var(--t-line)',
                }}>
                    <img src={IMG('sokcho')} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 'var(--t-font-body-lg)', fontWeight: 'var(--t-weight-bold)' }}>
                        강원 2박 3일
                    </span>
                    <span style={{
                        display: 'block', marginTop: 2, color: 'var(--t-ink-3)',
                        fontSize: 'var(--t-font-label)',
                    }}>
                        속초해변 · 설악산 · 대포항 외 4곳
                    </span>
                    <span style={{
                        display: 'flex', alignItems: 'center', gap: 4, marginTop: 'var(--t-space-2)',
                        color: 'var(--t-ink-3)', fontSize: 'var(--t-font-caption)',
                    }}>
                        <IcUsers size={12} /> 참여자 4명과 함께
                    </span>
                </span>
            </div>
        </Section>

        <Section divided>
            <TextField
                id="course-title"
                label="코스 제목"
                required
                placeholder="예) 속초 바다랑 설악산 다 보는 3일"
                value={title}
                onChange={(e) => setTitle(e.target.value.slice(0, 40))}
                maxLength={40}
                showCount
            />
            <TextArea
                id="course-summary"
                label="한 줄 소개"
                placeholder="따라갈 사람에게 도움이 될 한마디"
                value={summary}
                onChange={(e) => setSummary(e.target.value.slice(0, 100))}
                rows={2}
                maxLength={100}
                showCount
            />
        </Section>

        <Section divided last>
            <p style={{
                margin: 0, color: 'var(--t-ink-3)', fontSize: 'var(--t-font-label)', lineHeight: 1.6,
            }}>
                등록하면 이 지역을 찾는 사람들에게 보여집니다.
                일정·사진은 지금 상태 그대로 저장되고, 이후 모임을 바꿔도 코스는 그대로 남아요.
            </p>
        </Section>

        <ButtonFooter>
            <Button block>코스로 등록</Button>
        </ButtonFooter>
    </>
);
